import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useServer } from 'app/server/context'
import { useAuthentication } from 'authentication/context'
import { useCompaniesContext } from 'company/context'
import { AccountScreenProps } from 'home/stack/types'
import React, { useCallback, useState } from 'react'
import { Platform } from 'react-native'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { handleWebLink } from 'shared/utils'
import { AccountView } from 'users/account/view'
import { useUserAccount } from 'users/queries/account'
import { useUser } from 'users/queries/detail'
import { useRemoveUserPhoto } from 'users/queries/remove_photo'
import { useUpdateUserPhoto } from 'users/queries/update_photo'
import { WebScreenParams } from 'web/types'

export function AccountScreen({ navigation }: AccountScreenProps) {
  const { activeCompany } = useCompaniesContext()
  const { data: user } = useUser(undefined, activeCompany?.id)
  const { handleNavigation } = useRoutingContext()
  const { logout } = useAuthentication()
  const result = useUserAccount()
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const updateAvatarMutation = useUpdateUserPhoto()
  const removeAvatarMutation = useRemoveUserPhoto()
  const [isPhotoUploading, setIsPhotoUploading] = useState(false)
  const { localBaseURLInfo } = useServer()
  const isUserWebForCrew = Platform.OS === 'web' && !!user?.webForCrew

  const onAvatarChange = useCallback(
    async (avatarUrl: string, mimeType: string) => {
      setIsPhotoUploading(true)
      try {
        await updateAvatarMutation.mutateAsync({
          photoUrl: avatarUrl,
          mimeType,
        })
      } catch (err: any) {
        errorAlert(err, t('addMobileNumberErrorTitle'))
      }
      setIsPhotoUploading(false)
    },
    [updateAvatarMutation]
  )

  const onAvatarRemove = useCallback(async () => {
    await removeAvatarMutation.mutateAsync()
  }, [removeAvatarMutation])

  const onNameChange = useCallback(
    () => handleNavigation({ routeName: 'edit_username' }),
    [handleNavigation]
  )

  const onSettings = useCallback(
    () =>
      handleNavigation({
        routeName: 'settings',
        routeParams: { type: 'user' },
      }),
    [handleNavigation]
  )

  const onEditStoreSettings = useCallback(
    () =>
      handleNavigation({
        routeName: 'settings',
        routeParams: { type: 'store' },
      }),
    [handleNavigation]
  )

  const onLinkPress = useCallback(
    (webParams: WebScreenParams) => handleWebLink(navigation, webParams),
    [navigation]
  )

  const onDevTools = useCallback(
    () => handleNavigation({ routeName: 'dev_tools' }),
    [handleNavigation]
  )

  const onShareApp = useCallback(
    () => handleNavigation({ routeName: 'share_app' }),
    [handleNavigation]
  )

  const onLogout = useCallback(async () => {
    if (logout && user?.id) {
      setIsLoggingOut(true)
      await logout(user?.id)
    }
  }, [logout, user?.id])

  const { isLoading, error } = result

  return (
    <StatusBoundary
      loadingMessage={isLoggingOut ? t('signingOut') : undefined}
      isLoading={isLoading || isLoggingOut}
      error={error}
    >
      <AccountView
        user={result.data}
        isPhotoUploading={isPhotoUploading}
        onLogout={logout && onLogout}
        onAvatarChange={onAvatarChange}
        onAvatarRemove={onAvatarRemove}
        onNameChange={onNameChange}
        onSettings={onSettings}
        onDevTools={onDevTools}
        onShareApp={onShareApp}
        onLinkPress={onLinkPress}
        onEditStoreSettings={onEditStoreSettings}
        role={user?.role}
        localBaseURLEnvName={localBaseURLInfo?.envName}
        isUserWebForCrew={isUserWebForCrew}
        goldStars={
          user?.role === 'observer' || user?.role === 'admin'
            ? undefined
            : user?.goldStars
        }
      />
    </StatusBoundary>
  )
}
